/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { PersonIcon, CalendarIcon } from '@/shared/ui/radix-icons';
import { BuildingIcon } from '@/shared/ui/components/basic/icons';
import type { SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';

interface VisitPreviewCardProps {
  property: SearchAutocompleteOption | null;
  client: SearchAutocompleteOption | null;
  visitDate: string;
  visitTime: string;
}

/**
 * Formats an ISO date string (`YYYY-MM-DD`) as `DD/MM/YYYY`.
 *
 * @param dateStr - The ISO date string, or empty.
 * @returns The localized `DD/MM/YYYY` string, or empty when no date.
 */
function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

const CARD_STYLE = {
  padding: 'var(--space-3-5) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-secondary)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  fontFamily: 'var(--font-family)',
} as const;

const HEADING_STYLE = {
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 2,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
} as const;

const ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 'var(--space-2)' } as const;
const TITLE_STYLE = { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' } as const;
const SUB_STYLE = { fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' } as const;
const ICON_STYLE = { color: 'var(--text-secondary)', flexShrink: 0 } as const;

type PreviewRowProps = { icon: React.ReactNode; title: string; sublabel?: string };

/**
 * Single icon + title (+ optional sublabel) row inside the preview card.
 *
 * @param props - The leading icon, the primary title, and an optional sublabel.
 * @returns The preview row.
 */
function PreviewRow({ icon, title, sublabel }: PreviewRowProps) {
  return (
    <div style={ROW_STYLE}>
      {icon}
      <div>
        <div style={TITLE_STYLE}>{title}</div>
        {sublabel && <div style={SUB_STYLE}>{sublabel}</div>}
      </div>
    </div>
  );
}

/**
 * Live summary card that mirrors the current visit form selection (property,
 * client, and date/time). Renders nothing until at least one field is filled.
 *
 * @param props - The selected property/client options and the visit date/time.
 * @returns The preview card, or `null` when the form is empty.
 */
export function VisitPreviewCard({ property, client, visitDate, visitTime }: VisitPreviewCardProps) {
  const { t } = useTranslation('features/visits');
  const isEmpty = !property && !client && !visitDate && !visitTime;
  if (isEmpty) return null;

  const timeSuffix = visitTime ? ` ${t('drawer.previewAt', { time: visitTime })}` : '';

  return (
    <div style={CARD_STYLE}>
      <div style={HEADING_STYLE}>{t('drawer.previewSummary')}</div>
      {property && (
        <PreviewRow
          icon={<BuildingIcon width={14} height={14} style={ICON_STYLE} />}
          title={property.label}
          sublabel={property.sublabel}
        />
      )}
      {client && (
        <PreviewRow
          icon={<PersonIcon width={14} height={14} style={ICON_STYLE} />}
          title={client.label}
          sublabel={client.sublabel}
        />
      )}
      {(visitDate || visitTime) && (
        <PreviewRow
          icon={<CalendarIcon width={14} height={14} style={ICON_STYLE} />}
          title={`${formatDate(visitDate)}${timeSuffix}`}
        />
      )}
    </div>
  );
}
